import * as Yup from 'yup';
import {FormNameEnum} from 'enums/form';
import {errorMessages} from 'utils/constants';
import {IFileTypeRecognition, IFileVersion, IInputFile} from 'models';
import {getIsBeforeDate} from 'utils/common';
import {
  DIRECTORY_LINK_FORMAT_MESSAGE,
  isRegisteredResourceFieldRequired,
  isValidDirectoryLink,
} from 'admin/resources/utils';

export const buildEditResourceBaseMetadataValidationsShape = (createdUtc?: string | null) => {
  const requireDisplayName = isRegisteredResourceFieldRequired(FormNameEnum.displayName, createdUtc);
  const requireFileType = isRegisteredResourceFieldRequired(FormNameEnum.fileType, createdUtc);
  const requireLanguages = isRegisteredResourceFieldRequired(FormNameEnum.languages, createdUtc);
  const requireStudentFacing = isRegisteredResourceFieldRequired(FormNameEnum.isAccessibleToStudents, createdUtc);
  const requireWebLink = isRegisteredResourceFieldRequired(FormNameEnum.webLink, createdUtc);
  const requireUploadPermissions = isRegisteredResourceFieldRequired(FormNameEnum.hasUploadPermissions, createdUtc);
  const requireDirectoryLink = isRegisteredResourceFieldRequired(FormNameEnum.link, createdUtc);
  const requirePrograms = isRegisteredResourceFieldRequired(FormNameEnum.programs, createdUtc);
  const requireBriefDescription = isRegisteredResourceFieldRequired(FormNameEnum.briefDescription, createdUtc);
  const requireKeyTerms = isRegisteredResourceFieldRequired(FormNameEnum.keyTerms, createdUtc);

  let displayNameSchema = Yup.string().nullable();
  if (requireDisplayName) {
    displayNameSchema = Yup.string().required(errorMessages.requiredWithInputName('an Asset Name'));
  }

  let fileTypeSchema = Yup.number().nullable();
  if (requireFileType) {
    fileTypeSchema = Yup.number().required(errorMessages.requiredWithFieldName('an Asset Type'));
  }

  let languagesSchema: Yup.ArraySchema<number> = Yup.array().of(Yup.number());
  if (requireLanguages) {
    languagesSchema = languagesSchema.required('You should select at least one language!');
  }

  let studentFacingSchema = Yup.string().nullable();
  if (requireStudentFacing) {
    studentFacingSchema = Yup.string().required(errorMessages.requiredWithFieldName('Student Facing'));
  }

  let webLinkSchema = Yup.string().nullable().url('Web link must be a valid url!');
  if (requireWebLink) {
    webLinkSchema = webLinkSchema.test('is-web-link', errorMessages.requiredWithInputName('a Web Link'), function () {
      const {fileTypeRecognition, webLink} = this.parent;

      if (fileTypeRecognition?.isWebLink) {
        return !!webLink;
      }

      return true;
    });
  }

  let uploadPermissionsSchema = Yup.boolean();
  if (requireUploadPermissions) {
    uploadPermissionsSchema = uploadPermissionsSchema.test(
      'is-checked',
      'You should accept upload file permissions!',
      function () {
        const {file, hasUploadPermissions} = this.parent;
        const isHasUploadPermissionsTruthy = hasUploadPermissions === true;

        if (file) {
          return isHasUploadPermissionsTruthy;
        }

        return true;
      }
    );
  }

  let briefDescriptionSchema = Yup.string().nullable();
  if (requireBriefDescription) {
    briefDescriptionSchema = briefDescriptionSchema.required(
      errorMessages.requiredWithInputName('a Brief Description')
    );
  }

  let programsSchema: Yup.ArraySchema<number> = Yup.array().of(Yup.number());
  if (requirePrograms) {
    programsSchema = programsSchema.min(1, errorMessages.requiredWithFieldName('a Learning Experience'));
  }

  let keyTermsSchema = Yup.string().nullable();
  if (requireKeyTerms) {
    keyTermsSchema = keyTermsSchema.required(errorMessages.requiredWithInputName('Keywords'));
  }

  let directoryLinkSchema = Yup.string()
    .nullable()
    .test('is-directory-link-format', DIRECTORY_LINK_FORMAT_MESSAGE, (value) => isValidDirectoryLink(value));
  if (requireDirectoryLink) {
    directoryLinkSchema = directoryLinkSchema.required(errorMessages.requiredWithInputName('a Directory Link'));
  }

  return {
    [FormNameEnum.displayName]: displayNameSchema,
    [FormNameEnum.fileType]: fileTypeSchema,
    [FormNameEnum.gradeLevels]: Yup.array().of(Yup.number()),
    [FormNameEnum.pillars]: Yup.array().of(Yup.number()),
    [FormNameEnum.competencies]: Yup.array().of(Yup.number()),
    [FormNameEnum.languages]: languagesSchema,
    [FormNameEnum.keyTerms]: keyTermsSchema,
    [FormNameEnum.isAccessibleToStudents]: studentFacingSchema,
    [FormNameEnum.briefDescription]: briefDescriptionSchema,
    [FormNameEnum.programs]: programsSchema,
    [FormNameEnum.knowledgeSkillsAndAbilities]: Yup.array(),
    [FormNameEnum.expirationDate]: Yup.date()
      .nullable()
      .typeError('Expected date format: (mm/dd/yyyy)')
      .test('is-date-earlier-than-now', 'The selected date should be in the future.', function () {
        const {expirationDate} = this.parent;

        // If the datepicker is cleaned up, then validation is true
        if (expirationDate === null) {
          return true;
        }

        function isBefore(expirationDate: Date): boolean {
          const dateIsBeforeToday = getIsBeforeDate(expirationDate);

          if (dateIsBeforeToday) {
            return false;
          }
          return true;
        }

        if (expirationDate instanceof Date) {
          return isBefore(expirationDate);
        }

        // If expirationDate is not null and not a Date, let Yup handle the type error
        return true;
      }),
    [FormNameEnum.learningObjectives]: Yup.string().nullable(),
    [FormNameEnum.time]: Yup.number()
      .nullable()
      .min(0, 'Time should be a positive number!')
      .max(1440, 'Time should be a positive number smaller than 1440!'),
    [FormNameEnum.webLink]: webLinkSchema,
    [FormNameEnum.fileVersion]: Yup.string().nullable(),
    [FormNameEnum.hasUploadPermissions]: uploadPermissionsSchema,
    [FormNameEnum.canEdit]: Yup.boolean(),
    [FormNameEnum.link]: directoryLinkSchema,
  };
};

/** @deprecated Prefer buildEditResourceBaseMetadataValidationsShape(createdUtc) */
export const editResourceBaseMetadataValidationsShape = buildEditResourceBaseMetadataValidationsShape();

export interface IEditFileMetadataFormBaseValues {
  [FormNameEnum.fileType]: number;
  [FormNameEnum.displayName]: string;
  [FormNameEnum.gradeLevels]: number[];
  [FormNameEnum.competencies]: number[];
  [FormNameEnum.languages]: number[];
  [FormNameEnum.keyTerms]: string;
  [FormNameEnum.isAccessibleToStudents]: string;
  [FormNameEnum.briefDescription]: string;
  [FormNameEnum.programs]: number[];
  [FormNameEnum.pillars]: number[];
  [FormNameEnum.knowledgeSkillsAndAbilities]: Array<number | {id: number; name?: string}>;
  [FormNameEnum.expirationDate]: Date | string;
  [FormNameEnum.learningObjectives]: string;
  [FormNameEnum.shouldDisableFileType]: boolean;
  [FormNameEnum.time]?: number;
  [FormNameEnum.webLink]?: string;
  [FormNameEnum.file]?: IInputFile;
  [FormNameEnum.fileVersion]?: string;
  [FormNameEnum.hasUploadPermissions]?: boolean;
  [FormNameEnum.canEdit]?: boolean;
  [FormNameEnum.fileTypeRecognition]?: IFileTypeRecognition;
  [FormNameEnum.versions]?: IFileVersion[];

  [FormNameEnum.stockImageLinkNumber]?: string;
  [FormNameEnum.permissionNeeded]?: boolean | string | null;
  [FormNameEnum.imageSources]?: number[];
  [FormNameEnum.ethnicities]?: number[];
  [FormNameEnum.genders]?: number[];
  [FormNameEnum.publishedOutputTool]?: number;
  [FormNameEnum.alternateText]?: string;
  [FormNameEnum.linkToStockImageSource]?: string;

  [FormNameEnum.slideNumber]?: number;
  [FormNameEnum.sessionThemeNumber]?: number;

  [FormNameEnum.associatedResources]?: Array<string | {id: string; name?: string}>;
  [FormNameEnum.numberOfResources]?: number;
  [FormNameEnum.resourceIds]?: string[];
  [FormNameEnum.characterTraits]?: number[];
  [FormNameEnum.behavioralIndicators]?: number[];
  [FormNameEnum.videoTranscriptionLink]?: string;
  [FormNameEnum.credentialing]?: boolean | string | null;
  [FormNameEnum.permissionInPerpetuity]?: boolean;
  [FormNameEnum.accessibilityStatus]?: number | null;
  [FormNameEnum.accessibilityReports]?: string;

  [FormNameEnum.urlSource]?: string;
  [FormNameEnum.usageType]?: number;
  [FormNameEnum.reuseFlag]?: boolean | string | null;
  [FormNameEnum.notes]?: string;
  [FormNameEnum.permissionAgreementUrl]?: string;

  [FormNameEnum.id]?: string;
  [FormNameEnum.fileName]?: string;
  [FormNameEnum.link]?: string;
}

export const buildEditFileMetadataValidationSchema = (createdUtc?: string | null) =>
  Yup.object().shape({
    ...buildEditResourceBaseMetadataValidationsShape(createdUtc),
    [FormNameEnum.stockImageLinkNumber]: Yup.string().nullable(),
    [FormNameEnum.fileName]: Yup.string().nullable(),
    [FormNameEnum.id]: Yup.string().nullable(),
    [FormNameEnum.permissionNeeded]: Yup.mixed().nullable(),
    [FormNameEnum.imageSources]: Yup.array().of(Yup.number()),
    [FormNameEnum.ethnicities]: Yup.array().of(Yup.number()),
    [FormNameEnum.genders]: Yup.array().of(Yup.number()),
    [FormNameEnum.publishedOutputTool]: Yup.number().nullable(),
    [FormNameEnum.alternateText]: Yup.string().nullable(),
    [FormNameEnum.linkToStockImageSource]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Link to Stock Image Source must be a valid url!'),
    [FormNameEnum.slideNumber]: Yup.number().min(0).max(999).nullable(),
    [FormNameEnum.sessionThemeNumber]: Yup.number().nullable(),
    [FormNameEnum.associatedResources]: Yup.array(),
    [FormNameEnum.numberOfResources]: Yup.number().nullable().min(0),
    [FormNameEnum.resourceIds]: Yup.array().of(Yup.string()),
    [FormNameEnum.characterTraits]: Yup.array().of(Yup.number()),
    [FormNameEnum.behavioralIndicators]: Yup.array().of(Yup.number()),
    [FormNameEnum.videoTranscriptionLink]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Video Transcript Link must be a valid url!'),
    [FormNameEnum.credentialing]: Yup.mixed().nullable(),
    [FormNameEnum.permissionInPerpetuity]: Yup.boolean(),
    [FormNameEnum.accessibilityStatus]: Yup.mixed().nullable(),
    [FormNameEnum.accessibilityReports]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Accessibility Reports must be a valid url!'),
    [FormNameEnum.urlSource]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Media Rights URL Source must be a valid url!'),
    [FormNameEnum.usageType]: Yup.number().nullable(),
    [FormNameEnum.reuseFlag]: Yup.mixed().nullable(),
    [FormNameEnum.notes]: Yup.string().nullable(),
    [FormNameEnum.permissionAgreementUrl]: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .url('Permission Agreement URL must be a valid url!'),
  });

/** Default schema (enforces all required fields — same as a new record). */
export const editFileMetadataValidationSchema = buildEditFileMetadataValidationSchema();
